import locale from "localization";
import Button from "../button/button";
import { useEffect, useRef, useState } from "react";
import styles from "./select-file.module.scss";
import Image from "../image/image";
import Text from "../text/text";
import { isBoolean, isString } from "lodash";
import { StocqIcon } from "images";
import { Warning } from "@material-ui/icons";
import Dropzone from "react-dropzone";

export const extractDirectoryWithFile = (url) => {
  const match = url.match(/.+\/([^/]+\.[a-z]+)(?=\/|$)/i);
  return match ? match[1] : null;
};

export const extractFileSizeFromUrl = (url) => {
  if (!isString(url)) return "0.00 KB";
  const match = url.match(/_([0-9]+(?:\.[0-9]+)?)(KB|MB)\./);
  return match ? match[1] + match[2] : "0.00 KB";
};

const SelectFile = (props) => {
  const refFile = useRef(null);
  const [isPreview, setIsPreview] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [error, setError] = useState();
  const [urlValue, setUrlValue] = useState(isString(props?.value) ? props?.value : null);

  useEffect(() => {
    if (props?.value instanceof File) {
      setFileData({
        name: props?.value.name,
        size: `${(props?.value.size / 1024).toFixed(2)} KB`,
        preview: URL.createObjectURL(props?.value),
      });
      setUrlValue(URL.createObjectURL(props?.value));
    }

    if (isString(props?.value)) {
      setUrlValue(props?.value);
      setFileData(null);
    }

    if (!props?.value) {
      setUrlValue("");
      setFileData(null);
    }

    // ✅ Clear the input's value when props.value changes
    if (refFile?.current) {
      refFile.current.value = "";
    }
  }, [props?.value]);

  const accept = props?.type === "image" ? "image/*" : undefined;
  const validatedDimension = isBoolean(props?.validatedDimension)
    ? props?.validatedDimension
    : true;
  const renderResult = () => {
    if (error) return;
    if (props?.renderResult) {
      return props?.renderResult();
    }

    return (
      <>
        {isPreview && (
          <div className={styles.preview}>
            <Image
              onClick={() => {
                setIsPreview(false);
              }}
              src={urlValue || fileData?.preview}
              alt={fileData?.name}
            />
          </div>
        )}
        <div className={styles.result}>
          <Image
            onClick={() => {
              setIsPreview(true);
            }}
            src={urlValue || fileData?.preview}
            alt={fileData?.name}
          />
          <div>
            <Text className={styles.fileName}>
              {fileData?.name || extractDirectoryWithFile(urlValue)}
            </Text>
            <Text className={styles.fileSize}>
              {fileData?.size || extractFileSizeFromUrl(props?.value)}
            </Text>
          </div>
        </div>
      </>
    );
  };

  const handleFileChange = (event) => {
    setError(null);
    const selectedFile = event?.length > 0 ? event[0] : event.target.files[0];
    if (selectedFile) {
      const validFileTypes =
        props?.type === "image" ? ["image/jpeg", "image/jpg", "image/png"] : [];
      if (validFileTypes?.length > 0 && !validFileTypes.includes(selectedFile.type)) {
        props?.onChange?.(props?.name, { value: null, error: true });
        setError(props?.errorFileFormatMessage || locale.invalidFileType);
        return;
      }

      const maxSizeKb = props?.maxSizeKb || Infinity;
      const fileSizeKb = selectedFile.size / 1024;

      if (fileSizeKb > maxSizeKb) {
        props?.onChange?.(props?.name, { value: null, error: true });
        setError(
          props?.errorFileSizeMessage || `${locale.fileTooLarge} (${fileSizeKb.toFixed(2)} KB)`
        );
        return;
      }

      if (props?.type === "image") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            if (validatedDimension && (img.width > 100 || img.height > 100)) {
              props?.onChange?.(props?.name, { value: null, error: true });
              setError(locale.invalidDimensions);
              return;
            }

            props?.onChange?.(props?.name, { value: selectedFile });
          };
          img.onerror = () => {
            props?.onChange?.(props?.name, { value: null, error: true });
            setError(locale.invalidImage);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFileData({
          name: selectedFile.name,
          size: `${(selectedFile.size / 1024).toFixed(2)} KB`,
          preview: URL.createObjectURL(selectedFile),
        });
      }
    }
  };

  return (
    <div className={`${styles.content}`}>
      {!props?.isView && (
        <Dropzone onDrop={handleFileChange} multiple={props?.multiple}>
          {({ getRootProps, getInputProps, isDragActive }) => {
            return (
              <div
                {...getRootProps()}
                className={`${styles.dropzone} ${isDragActive ? styles.dragActive : ""}`}
              >
                <input
                  {...getInputProps()}
                  type="file"
                  hidden
                  ref={refFile}
                  onChange={handleFileChange}
                  accept={accept}
                />
                <Button
                  onClick={() => {
                    if (error) {
                      setError(null);
                    }
                    props?.onClick?.();
                    refFile?.current?.click();
                  }}
                  primary
                  style={{
                    backgroundColor: "#eae8ff",
                    color: "#5349b7",
                    fontWeight: "500",
                    ...props?.buttonProps?.style,
                  }}
                  {...props?.buttonProps}
                >
                  {urlValue || fileData ? locale.replace : props?.text || locale.browseYourFiles}
                </Button>
                {error && (
                  <Text
                    subtitle
                    color={"red"}
                    style={{
                      marginTop: "5px",
                      width: "160px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      ...props?.errorStyle,
                    }}
                  >
                    <Warning fontSize="small" style={{ fontSize: "12px" }} /> <span>{error}</span>
                  </Text>
                )}
              </div>
            );
          }}
        </Dropzone>
      )}
      {urlValue || fileData
        ? renderResult()
        : !props?.renderResult && (
            <div className={styles.result}>
              <Image src={StocqIcon} alt="No file selected" />
              <Text>{locale.noFileSelected}</Text>
            </div>
          )}
    </div>
  );
};

export default SelectFile;
